import { ArcRotateCamera, Camera, Color3, CubeTexture, DefaultRenderingPipeline, DirectionalLight, Engine, FreeCamera, HemisphericLight, LinesMesh, Matrix, Mesh, MeshBuilder, NullEngine, PBRMaterial, Scene, SceneLoader, ShadowGenerator, StandardMaterial, TargetCamera, Texture, Tools, TransformNode, Vector2, Vector3, WebGPUEngine, WebXRDefaultExperience, WebXRFeatureName, WebXRMotionControllerTeleportation } from '@babylonjs/core';
import '@babylonjs/loaders';
import { GeolocationHelper } from './address-to-geoloc';
import { polyline } from './polyline';
import { UtilityService } from './services/utility.service';
import { AxisService } from './babylon-helpers/axis.service';
import { ModelLoaderService } from './babylon-helpers/model-loader.service';
import { ContextService } from './babylon-helpers/context.service';
import { TileService } from './babylon-helpers/tile.service';
import { GridMaterial } from '@babylonjs/materials';
import { ImageTileService } from './babylon-helpers/image-tile.service';
import { Point3 } from './models/point.type';
import { GUI3DManager, HolographicSlate, NearMenu, TouchHolographicButton } from '@babylonjs/gui/3D';
import { StackPanel, TextBlock, Grid, Control, RadioButton } from '@babylonjs/gui';
import { CloudStorageService } from './firebase/cloud-storage.service';
import { GroupControlsModel } from './group-controls.model';

interface StackRadio {
    stackPanel: StackPanel,
    button: RadioButton
}

enum buildingTypes {
    corridor = 'Corridor',
    hybrid = 'Hybride',
    gallery = 'Galerij',
    tower = 'Toren',
    rowhouse = 'Rijwoning'
}
export class SceneManager {

    private axisService: AxisService;
    contextService: ContextService;
    imageTileService: ImageTileService;
    modelLoaderService: ModelLoaderService;
    tileService: TileService;

    private engine?: Engine | WebGPUEngine;
    private camera?: ArcRotateCamera;
    private scene?: Scene;
    private shadowGenerator?: ShadowGenerator;
    private skybox?: Mesh;
    private skyboxTexture?: CubeTexture;
    private skyboxMaterial: StandardMaterial;
    private skyboxMaterialGrey: StandardMaterial;
    private gridGround: Mesh;
    private activeVRModel: Mesh;
    private defaultCameraName = 'ArcRotateCamera';
    private screenShotCameraName = 'ScreenShotCamera';


    posOrRotOfGroupChangedCallback: (res: {
        position: Point3,
        rotation: Point3,
        groupId: number | string;
    }) => void;

    pickedGroupCallback: (groupId: string | number) => void;
    scaleChangedCallback: (res: { scale: Point3, groupId: string | number }) => void;
    pickedContextItemCallback: (res: { id: string | number }) => void;
    hoverContextItemCallback: (id: string | number) => void;

    polylinePoints: Vector3[] = [];
    polylineBorder?: Mesh;
    drawingPolyline: boolean = false;
    enabledHoverTracking: boolean = false;

    geoLoc: GeolocationHelper;
    utilityService: UtilityService;
    nullEngine = false;

    constructor(utilityService: UtilityService, geoLoc: GeolocationHelper, nullEngine = false) {
        this.utilityService = utilityService;
        this.geoLoc = geoLoc;
        this.nullEngine = nullEngine;
    }

    enabledRendering = true;

    startDrawPolyline() {
        this.resetPolyline();
        let findPolylineHolder = this.scene?.getMeshByName('polylineHolder');
        if (!findPolylineHolder) {
            findPolylineHolder = MeshBuilder.CreateBox('polylineHolder', { size: 0 });
            this.scene?.addMesh(findPolylineHolder);
        }
        else {
            const s = findPolylineHolder.getChildMeshes();
            s.forEach(child => {
                this.scene?.removeMesh(child);
                child.dispose();
            });
        }
        this.modelLoaderService.disableGizmosInAllGroupControls();
    }

    showPolyline(points: number[][]) {
        this.polylinePoints = points.map(pt => new Vector3(pt[0], pt[1], pt[2]));
        if (this.polylineBorder) {
            this.polylineBorder.dispose();
        }
        this.polylineBorder = polyline("line", { path: this.polylinePoints, width: 50, closed: true }, this.scene);
        const mat = new StandardMaterial('dafa', this.scene as Scene);
        mat.diffuseColor = new Color3(0, 0, 1);

        this.polylineBorder.material = mat;
        this.polylineBorder.position.y = 0.5;
    }

    stopDraw(): Point3[] {
        this.drawingPolyline = false;
        this.modelLoaderService.enableGizmosInAllGroupControls();
        return this.polylinePoints.map(p => [p.x, p.y, p.z]);
    }

    resetPolyline() {
        this.cleanPolyline();
        this.drawingPolyline = true;
    }

    undoLastPolylinePoint() {
        this.polylinePoints.pop();
        let findPolylineHolder = this.scene?.getMeshByName('polylineHolder');
        if (findPolylineHolder) {
            const s = findPolylineHolder.getChildMeshes();
            if (s.length > 0) {
                const lastMesh = s.pop();
                this.scene?.removeMesh(lastMesh);
                lastMesh.dispose();
            }
        }
        this.createPolyline();
    }

    cleanPolyline() {
        this.drawingPolyline = false;
        if (this.polylineBorder) {
            this.polylineBorder.dispose();
        }
        this.polylineBorder = undefined;
        this.polylinePoints = [];
        let findPolylineHolder = this.scene?.getMeshByName('polylineHolder');
        if (findPolylineHolder) {
            const s = findPolylineHolder.getChildMeshes();
            s.forEach(child => {
                this.scene?.removeMesh(child);
                child.dispose();
            });
        }
    }

    disposeScene() {
        this.scene?.materials.forEach(material => {
            material.dispose();
        })
        this.scene?.lights.forEach(light => {
            light.dispose();
        })
        this.scene?.meshes.forEach(mesh => {
            mesh.dispose();
        })
        if (this.modelLoaderService) {
            this.modelLoaderService.disposeMeshCache();
        }
        this.scene?.cameras.forEach(cam => cam.dispose());
        this.scene?.dispose();
        this.engine?.dispose();
        this.scene = undefined;
        this.engine = undefined;
    }

    async initScene(canvas: WebGLRenderingContext) {
        // const isWebGPU = await WebGPUEngine.IsSupportedAsync;
        const isWebGPU = false;
        if (this.nullEngine) {
            this.engine = new NullEngine();
        } else {
            if (isWebGPU) {
                this.engine = new WebGPUEngine(canvas as any, {
                    antialias: true,
                });
                await (this.engine as WebGPUEngine).initAsync();
                this.engine.setHardwareScalingLevel(0.5);
            } else {
                this.engine = new Engine(canvas, true);
                this.engine.setHardwareScalingLevel(0.5);
            }
        }

        this.scene = new Scene(this.engine);

        this.scene.clearColor = Color3.FromHexString('#ffffff') as any;

        this.scene.useRightHandedSystem = true;
        this.scene.autoClear = false; // Color buffer
        this.scene.autoClearDepthAndStencil = false;
        // dirty mechanism was causing problems with glb files, read more: https://forum.babylonjs.com/t/load-multiple-files-fast/12194/8
        this.scene.blockMaterialDirtyMechanism = true;
        // this.scene.performancePriority = ScenePerformancePriority.BackwardCompatible;

        this.camera = new ArcRotateCamera(this.defaultCameraName, 0, 0, 10, new Vector3(0, 0, 0), this.scene);

        this.camera.panningSensibility = 8;
        this.camera.wheelPrecision = 2;
        this.camera.setPosition(new Vector3(350, 200, 350));
        this.camera.attachControl(canvas, true);
        this.camera.checkCollisions = true;
        this.camera.maxZ = 20000;


        const boxForGroundCollisions = MeshBuilder.CreateBox('invisibile-ground-collision-body', { width: 10000, height: 3, depth: 10000 }, this.scene);
        boxForGroundCollisions.checkCollisions = true;
        boxForGroundCollisions.isVisible = false;
        boxForGroundCollisions.isPickable = false;


        // const env = this.scene.createDefaultEnvironment({
        //     createGround: false,
        //     createSkybox: false,
        //     toneMappingEnabled: false,
        //     setupImageProcessing: true,
        //     environmentTexture: 'https://playground.babylonjs.com/textures/Studio_Softbox_2Umbrellas_cube_specular.env',
        //     cameraExposure: 1,
        //     cameraContrast: 1,
        // });

        // this.scene.environmentTexture.updateSamplingMode(3)

        // var pipeline = new DefaultRenderingPipeline(
        //     "defaultPipeline",
        //     true, // TODO check why this had to be set to false after 5.22.0 update in order to fix FPS drop on DEV build
        //     this.scene,
        //     [this.camera]
        // );

        // pipeline.imageProcessingEnabled = false;
        // if (!this.nullEngine) {
        //     // pipeline.samples = 4;
        // }

        const text = new CubeTexture("/textures/tropical_sunny_day/environment.env", this.scene);
        this.skybox = this.scene.createDefaultSkybox(text, true, 10000, 0.1, true);
        this.skybox.rotation.y = -Math.PI / 2;
        this.scene.environmentIntensity = 1;

        // this.skybox = MeshBuilder.CreateBox("skyBox", { size: 10000.0 }, this.scene);
        // this.skybox.isPickable = false;
        // this.skybox.infiniteDistance = false;
        // this.skyboxMaterial = new StandardMaterial("skyBox", this.scene);
        // this.skyboxMaterial.backFaceCulling = false;
        // this.skyboxTexture = new CubeTexture("/assets/textures/TropicalSunnyDay", this.scene)
        // this.skyboxMaterial.reflectionTexture = this.skyboxTexture;
        // this.skyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;
        // this.skyboxMaterial.diffuseColor = new Color3(0, 0, 0);
        // this.skyboxMaterial.specularColor = new Color3(0, 0, 0);
        // this.skyboxMaterial.disableLighting = true;

        // this.skyboxMaterialGrey = new StandardMaterial("skyBox", this.scene);
        // this.skyboxMaterialGrey.backFaceCulling = false;
        // this.skybox.infiniteDistance = false;
        // this.skyboxMaterialGrey.diffuseColor = new Color3(1, 1, 1);
        // this.skyboxMaterialGrey.emissiveColor = new Color3(1, 1, 1);
        // this.skyboxMaterialGrey.specularColor = new Color3(1, 1, 1);
        // this.skyboxMaterialGrey.disableLighting = true;
        // this.skybox.renderingGroupId = 0;

        // this.skybox.material = this.skyboxMaterialGrey;

        this.skybox.onBeforeBindObservable.add(() => {
            if (this.scene) {
                this.tileService.cleanClippingPlanesFromScene();
            }
        })

        this.skybox.onAfterRenderObservable.add(() => {
            if (this.scene) {
                this.tileService.assignClippingPlanesToScene();
            }
        })

        const shadowLight = new DirectionalLight('pointLight', new Vector3(-100, -200, -100), this.scene);
        // shadowLight.position = new Vector3(0, 100, 100);
        // shadowLight.diffuse = new Color3(1, 1, 1);
        shadowLight.shadowMinZ = 0;
        shadowLight.shadowMaxZ = 1200;
        // shadowLight.intensity = 1.4;
        shadowLight.intensity = 3;

        shadowLight.position = new Vector3(300, 300, 300);

        const hemisphereLight = new HemisphericLight('hemisphericLight', new Vector3(0, 1, 0), this.scene);
        hemisphereLight.diffuse = new Color3(1, 1, 1);
        // hemisphereLight.intensity = 0.2;
        hemisphereLight.intensity = 0.2;

        this.shadowGenerator = new ShadowGenerator(3000, shadowLight, false);
        this.shadowGenerator.getShadowMap().refreshRate = 100;

        this.shadowGenerator.setDarkness(0.6);
        this.shadowGenerator.bias = 0.0005;
        this.shadowGenerator.normalBias = 0.3;

        // this.shadowGenerator.useKernelBlur = true;
        this.shadowGenerator.usePercentageCloserFiltering = true;


        this.axisService = new AxisService();
        this.modelLoaderService = new ModelLoaderService(this.scene, this.shadowGenerator)
        this.contextService = new ContextService(this.modelLoaderService, this.scene, this.shadowGenerator);
        this.imageTileService = new ImageTileService(this.scene);
        this.tileService = new TileService(this.scene, this.utilityService);

        var groundMaterial = new GridMaterial("defaultgridmaterial", this.scene);
        groundMaterial.majorUnitFrequency = 10;
        groundMaterial.gridRatio = 10;
        groundMaterial.backFaceCulling = true;
        groundMaterial.mainColor = new Color3(1, 1, 1);
        groundMaterial.lineColor = Color3.FromHexString('#b5e1dc');
        groundMaterial.opacity = 0.98;
        this.gridGround = MeshBuilder.CreateBox("gridground", { width: 10000, height: 0.1, depth: 10000 }, this.scene);
        this.gridGround.position.y = -0.3;
        this.gridGround.isPickable = false;
        this.gridGround.material = groundMaterial;

        this.scene.onPointerUp = (p) => {
            const ray = this.scene.createPickingRay(this.scene.pointerX, this.scene.pointerY, Matrix.Identity(), this.camera);
            if (this.drawingPolyline && p.button === 0) {

                const mat = new StandardMaterial('dafa', this.scene);
                mat.diffuseColor = new Color3(0, 0, 1);

                const pickInfo = this.tileService.checkIntersectionAgainsTiledGround(ray);
                let findPolylineHolder = this.scene.getMeshByName('polylineHolder');
                let controlBox = MeshBuilder.CreateBox('polylineControlPoint', { size: 0.5 });

                if (pickInfo.pickedPoint) {
                    this.polylinePoints.push(pickInfo.pickedPoint);
                    controlBox.position = pickInfo.pickedPoint;
                }

                controlBox.material = mat;
                controlBox.parent = findPolylineHolder;

                this.createPolyline();
            }

            this.contextService.checkIntersectionWithContext(ray, this.pickedContextItemCallback);
        }

        this.scene.onPointerMove = (s => {
            if (this.enabledHoverTracking) {
                const ray = this.scene.createPickingRay(this.scene.pointerX, this.scene.pointerY, Matrix.Identity(), this.camera);
                this.contextService.checkHoverIntersectionWithContext(ray, this.hoverContextItemCallback)
            }
        });

        if (!this.nullEngine) {
            this.axisService.showWorldAxis(3, this.scene);
        }
        this.engine.runRenderLoop(() => {
            // this might reduce performance...
            if (this.enabledRendering) {
                this.scene?.render(true, true);
            }
        });

        // this.customMeshMaterial.freeze();

        window.addEventListener('resize', () => { this.resizeEngine() });
        return this.scene;
    }


    async initVRScene(canvas: WebGLRenderingContext, startBuildingType: string, startUnitType: string, buildingUnitChangeCallback: (bt, ut) => void) {

        // const xrPolyfillPromise = new Promise((resolve) => {
        //     if ((navigator as any).xr) {
        //         return resolve({});
        //     }
        //     if ((window as any).WebXRPolyfill) {
        //         new WebXRPolyfill();
        //         return resolve({});
        //     } else {
        //         const url = "https://cdn.jsdelivr.net/npm/webxr-polyfill@latest/build/webxr-polyfill.js";
        //         const s = document.createElement("script");
        //         s.src = url;
        //         document.head.appendChild(s);
        //         s.onload = () => {
        //             new WebXRPolyfill();
        //             resolve({});
        //         };
        //     }
        // });

        // await xrPolyfillPromise;

        if (this.nullEngine) {
            this.engine = new NullEngine();
        } else {
            this.engine = new Engine(canvas, true);
        }
        this.scene = new Scene(this.engine);
        let camera = new FreeCamera("camera1", new Vector3(0, 1.8, -20), this.scene);
        camera.setTarget(new Vector3(0, 1.8, 0));
        camera.minZ = 0;
        camera.attachControl(canvas, true);
        camera.speed = 0.05;
        // this.scene.clearColor = new Color4(0, 0, 0);

        this.scene.useRightHandedSystem = true;
        var light = new HemisphericLight("light1", new Vector3(0, 1, 0), this.scene);
        light.intensity = 0.5;

        const xr = await this.scene.createDefaultXRExperienceAsync();

        let envTex;
        if (startBuildingType && startUnitType) {
            envTex = CubeTexture.CreateFromPrefilteredData('../../../textures/environment.env', this.scene, false, false);
        } else {
            envTex = CubeTexture.CreateFromPrefilteredData('./textures/environment.env', this.scene, false, false);
        }
        this.scene.environmentTexture = envTex;
        this.scene.createDefaultSkybox(envTex, true, 200, 0.1);

        var pipeline = new DefaultRenderingPipeline(
            "defaultPipeline", // The name of the pipeline
            true, // Do you want the pipeline to use HDR texture?
            this.scene, // The scene instance
            [camera] // The list of cameras to be attached to
        );
        pipeline.samples = 4;

        const ground = MeshBuilder.CreateGround('ground', {
            width: 100, height: 100
        })
        const material = new PBRMaterial('da', this.scene);
        material.roughness = 1;
        ground.material = material;
        ground.receiveShadows = true;

        const shadowLight = new DirectionalLight('pointLight', new Vector3(-1, -1, 1), this.scene);
        shadowLight.diffuse = new Color3(1, 1, 1);
        shadowLight.intensity = 1;

        shadowLight.position = new Vector3(300, 300, 300);
        this.shadowGenerator = new ShadowGenerator(2056, shadowLight, false);

        this.shadowGenerator.usePercentageCloserFiltering = true;

        this.shadowGenerator.setDarkness(0);
        this.shadowGenerator.bias = 0.00004;
        this.shadowGenerator.normalBias = 0.07;
        this.modelLoaderService = new ModelLoaderService(this.scene, this.shadowGenerator)

        const featuresManager = xr.baseExperience.featuresManager;
        xr.teleportation = featuresManager.enableFeature(WebXRFeatureName.TELEPORTATION, "stable", {
            xrInput: xr.input,
            floorMeshes: [ground],
        }) as WebXRMotionControllerTeleportation;


        this.setupGUI(xr, startBuildingType, startUnitType, buildingUnitChangeCallback);

        this.engine.runRenderLoop(() => {
            this.scene?.render();
        });

        return this.scene;
    }

    private setupGUI(xr: WebXRDefaultExperience, startBuildingType: string, startUnitType: string, buildingUnitChangeCallback: (bt, ut) => void) {
        var manager = new GUI3DManager(this.scene);

        manager.useRealisticScaling = true;


        var slate = new HolographicSlate("down");

        slate.title = "MOOS MAKER";
        slate.minDimensions = new Vector2(11, 11);
        slate.dimensions = new Vector2(11, 11);
        slate.titleBarHeight = 1.2;

        manager.addControl(slate);

        const img = new Control('whateva');
        slate.content = img;
        xr.enterExitUI.activeButtonChangedObservable.add((b => {
            if (b.sessionMode === 'immersive-vr') {
                slate.defaultBehavior.followBehavior.defaultDistance = -0.6;
                slate.defaultBehavior.followBehavior.minimumDistance = -10;
                slate.defaultBehavior.followBehavior.maximumDistance = 0;
                slate.defaultBehavior.followBehavior.useFixedVerticalOffset = true;
                slate.defaultBehavior.followBehavior.fixedVerticalOffset = -4;
            } else if (b.sessionMode === 'inline') {

                slate.defaultBehavior.followBehavior.defaultDistance = 0.6;
                slate.defaultBehavior.followBehavior.minimumDistance = 0;
                slate.defaultBehavior.followBehavior.maximumDistance = 10;
                slate.defaultBehavior.followBehavior.useFixedVerticalOffset = false;
                slate.defaultBehavior.followBehavior.fixedVerticalOffset = 0;
            }
        }))


        slate.defaultBehavior.followBehaviorEnabled = true;

        img.host.background = 'white';
        (slate as any)._contentMaterial.alpha = 0.7;
        var advancedTexture = img.host;
        advancedTexture.background = 'transparent';

        var grid = new Grid();
        advancedTexture.addControl(grid);
        grid.background = 'transparent';

        grid.addColumnDefinition(0.5);
        grid.addColumnDefinition(0.5);
        grid.addRowDefinition(0.8);
        grid.addRowDefinition(0.2);

        var addRadioOptions = () => {

            const buildingButtons: StackRadio[] = [];
            let unitButtons: StackRadio[] = [];
            const groupBuildingType = 'buildingType';
            const groupModuleType = 'unitType';

            this.activeVRModel = new Mesh('vr');

            const handler = ({ state, button }) => {
                if (state) {

                    if (this.activeVRModel) {
                        this.activeVRModel.getChildMeshes().forEach(mesh => {
                            this.shadowGenerator.removeShadowCaster(mesh);
                        })
                        this.activeVRModel.dispose();
                    }

                    this.activeVRModel = new Mesh('vr');
                    if (button.group === groupBuildingType) {

                        const bt = button.name.split('-')[1] as buildingTypes;
                        unitButtons.forEach(s => {
                            s.stackPanel.dispose();
                            s.button.dispose();
                        });
                        switch (bt) {
                            case buildingTypes.corridor:
                                unitButtons = this.createCorridorPanel(grid, groupModuleType, handler, startUnitType)
                                break;
                            case buildingTypes.hybrid:
                                unitButtons = this.createHybridPanel(grid, groupModuleType, handler, startUnitType)
                                break;
                            case buildingTypes.gallery:
                                unitButtons = this.createGalleryPanel(grid, groupModuleType, handler, startUnitType)
                                break;
                            case buildingTypes.tower:
                                unitButtons = this.createTowerPanel(grid, groupModuleType, handler, startUnitType)
                                break;
                            default:
                                break;
                        }
                        const buildingType = button.name.split('-')[1];
                        const btn = this.findCheckedButton(unitButtons);
                        const unit = btn.name.split('-')[1];
                        this.loadVRModel(buildingType, unit, xr);
                        buildingUnitChangeCallback(buildingType, unit);
                    } else {
                        const activeBuildingTypeBtn = this.findCheckedButton(buildingButtons);
                        const buildingType = activeBuildingTypeBtn.name.split('-')[1];
                        const unit = button.name.split('-')[1];
                        this.loadVRModel(buildingType, unit, xr);
                        buildingUnitChangeCallback(buildingType, unit);
                    }
                }
            }

            var panel = new StackPanel(groupBuildingType);
            grid.addControl(panel, 0, 0);

            if (startBuildingType) {
                const btn3 = this.addRadio(buildingTypes.corridor, panel, groupBuildingType, handler,
                    startBuildingType.toLowerCase() === buildingTypes.corridor.toLowerCase());
                const btn2 = this.addRadio(buildingTypes.hybrid, panel, groupBuildingType, handler,
                    startBuildingType.toLowerCase() === buildingTypes.hybrid.toLowerCase());
                const btn1 = this.addRadio(buildingTypes.gallery, panel, groupBuildingType, handler,
                    startBuildingType.toLowerCase() === buildingTypes.gallery.toLowerCase());
                const btn4 = this.addRadio(buildingTypes.tower, panel, groupBuildingType, handler,
                    startBuildingType.toLowerCase() === buildingTypes.tower.toLowerCase());
                buildingButtons.push(...[btn1, btn2, btn3, btn4]);
            } else {
                const btn3 = this.addRadio(buildingTypes.corridor, panel, groupBuildingType, handler, true);
                const btn2 = this.addRadio(buildingTypes.hybrid, panel, groupBuildingType, handler);
                const btn1 = this.addRadio(buildingTypes.gallery, panel, groupBuildingType, handler);
                const btn4 = this.addRadio(buildingTypes.tower, panel, groupBuildingType, handler);
                buildingButtons.push(...[btn1, btn2, btn3, btn4]);
            }

            if (startBuildingType && startUnitType) {
                switch (startBuildingType) {
                    case buildingTypes.corridor.toLowerCase():
                        unitButtons = this.createCorridorPanel(grid, groupModuleType, handler, startUnitType)
                        break;
                    case buildingTypes.hybrid.toLowerCase():
                        unitButtons = this.createHybridPanel(grid, groupModuleType, handler, startUnitType)
                        break;
                    case buildingTypes.gallery.toLowerCase():
                        unitButtons = this.createGalleryPanel(grid, groupModuleType, handler, startUnitType)
                        break;
                    case buildingTypes.tower.toLowerCase():
                        unitButtons = this.createTowerPanel(grid, groupModuleType, handler, startUnitType)
                        break;
                    default:
                        break;
                }
                this.loadVRModel(startBuildingType, startUnitType, xr);
            } else {
                unitButtons = this.createCorridorPanel(grid, groupModuleType, handler, startUnitType)
            }
        }

        addRadioOptions();

        slate._gizmo.dispose();
    }

    private addRadio(text, parent: StackPanel, group: string, handler: (res: { state, button }) => void, isChecked?: boolean): { stackPanel: StackPanel, button: RadioButton } {

        const radioButtonSize = '20px';
        const radioFontSize = '20px';

        var button = new RadioButton(`${group}-${text}`);
        if (isChecked) {
            button.isChecked = isChecked;
        }
        button.width = radioButtonSize;
        button.thickness = 5;
        button.height = radioButtonSize;
        button.color = "white";
        button.background = "blue";
        button.group = group;
        button.checkSizeRatio = 0.7;

        button.onIsCheckedChangedObservable.add((state) => handler({ state, button }));

        var stackPanel: StackPanel = Control.AddHeader(button, text, "300px", { isHorizontal: true, controlFirst: true });
        stackPanel.height = '30px';
        stackPanel.paddingLeftInPixels = 200;
        stackPanel.color = 'white';
        stackPanel.fontSize = radioFontSize;

        parent.addControl(stackPanel);
        return { stackPanel, button };
    }

    createGalleryPanel(grid: Grid, groupModuleType: string, handler: (res: { state, button }) => void, startUnitType: string): StackRadio[] {
        var panel = new StackPanel(groupModuleType);
        panel.background = 'transparent';
        grid.addControl(panel, 0, 1);
        const unitButtons: StackRadio[] = [];
        const gbtn1 = this.addRadio("S", panel, groupModuleType, handler, startUnitType.toLowerCase() === 's');
        const gbtn2 = this.addRadio("M", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'm');
        const gbtn3 = this.addRadio("L", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'l');
        const gbtn4 = this.addRadio("XL", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'xl');
        const gbtn5 = this.addRadio("Mockup", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'mockup');
        unitButtons.push(...[gbtn1, gbtn2, gbtn3, gbtn4, gbtn5]);
        if (!unitButtons.some(s => s.button.isChecked)) {
            gbtn1.button.isChecked = true;
        }
        return unitButtons;
    }

    createHybridPanel(grid: Grid, groupModuleType: string, handler: (res: { state, button }) => void, startUnitType: string): StackRadio[] {
        var panel = new StackPanel(groupModuleType);
        panel.background = 'transparent';
        grid.addControl(panel, 0, 1);
        const unitButtons: StackRadio[] = [];
        const gbtn1 = this.addRadio("S", panel, groupModuleType, handler, startUnitType.toLowerCase() === 's');
        const gbtn2 = this.addRadio("M", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'm');
        const gbtn3 = this.addRadio("L", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'l');
        const gbtn4 = this.addRadio("XL", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'xl');
        const gbtn5 = this.addRadio("Mockup", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'mockup');

        unitButtons.push(...[gbtn1, gbtn2, gbtn3, gbtn4, gbtn5]);
        if (!unitButtons.some(s => s.button.isChecked)) {
            gbtn1.button.isChecked = true;
        }
        return unitButtons;
    }

    createTowerPanel(grid: Grid, groupModuleType: string, handler: (res: { state, button }) => void, startUnitType: string): StackRadio[] {
        var panel = new StackPanel(groupModuleType);
        panel.background = 'transparent';
        grid.addControl(panel, 0, 1);
        const unitButtons: StackRadio[] = [];
        const gbtn1 = this.addRadio("XS", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'xs');
        const gbtn2 = this.addRadio("S", panel, groupModuleType, handler, startUnitType.toLowerCase() === 's');
        const gbtn3 = this.addRadio("M", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'm');
        const gbtn4 = this.addRadio("L", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'l');
        const gbtn5 = this.addRadio("XL", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'xl');

        unitButtons.push(...[gbtn1, gbtn2, gbtn3, gbtn4, gbtn5]);
        if (!unitButtons.some(s => s.button.isChecked)) {
            gbtn1.button.isChecked = true;
        }
        return unitButtons;
    }

    createCorridorPanel(grid: Grid, groupModuleType: string, handler: (res: { state, button }) => void, startUnitType: string): StackRadio[] {
        var panel = new StackPanel(groupModuleType);
        panel.background = 'transparent';
        grid.addControl(panel, 0, 1);
        const unitButtons: StackRadio[] = [];
        const gbtn1 = this.addRadio("XS", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'xs');
        const gbtn2 = this.addRadio("S", panel, groupModuleType, handler, startUnitType.toLowerCase() === 's');
        const gbtn3 = this.addRadio("M", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'm');
        const gbtn4 = this.addRadio("L", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'l');
        const gbtn5 = this.addRadio("XL", panel, groupModuleType, handler, startUnitType.toLowerCase() === 'xl');
        unitButtons.push(...[gbtn1, gbtn2, gbtn3, gbtn4, gbtn5]);
        if (!unitButtons.some(s => s.button.isChecked)) {
            gbtn1.button.isChecked = true;
        }
        return unitButtons;
    }

    private findCheckedButton(stackRadios: StackRadio[]): RadioButton {
        return stackRadios.find(b => b.button.isChecked).button;
    };

    private loadVRModel(buildingType: string, unit: string, xr: WebXRDefaultExperience) {
        CloudStorageService.getEnvDownloadUrl(`/assets/units-vr/${buildingType.toLowerCase()}/${unit.toLocaleLowerCase()}.glb`).then(downloadUrl => {
            SceneLoader.ImportMeshAsync('', downloadUrl, '', this.scene).then((res) => {
                res.meshes.forEach(mesh => {
                    if (mesh.name.includes('3d_floor')) {
                        xr.teleportation.addFloorMesh(mesh);
                    }
                    if (mesh.material && (mesh.material as PBRMaterial).environmentIntensity) {
                        (mesh.material as PBRMaterial).environmentIntensity = 0.8;
                    }
                    mesh.receiveShadows = true;
                    this.shadowGenerator.addShadowCaster(mesh);
                    mesh.parent = this.activeVRModel;
                })
                this.shadowGenerator.recreateShadowMap();
            });
        });
    }

    private createButton(nearMenu: NearMenu, text: string) {
        var button = new TouchHolographicButton("buttonXS");
        var text1 = new TextBlock();
        text1.text = text;
        text1.fontSize = "70px";
        text1.color = "White";
        nearMenu.addButton(button);
        button.content = text1;
        button.scaling = new Vector3(0.7, 0.7, 0.7);
    }

    private createPolyline() {
        if (this.polylineBorder) {
            this.polylineBorder.dispose();
        }
        if (this.polylinePoints.length > 1) {
            var line = polyline("line", { path: this.polylinePoints, width: 50, closed: true }, this.scene);
            line.position.y = 0.5;
            this.polylineBorder = line;
        }
    }

    hideGroundGrid() {
        this.gridGround.isVisible = false;
    }

    showGroundGrid() {
        this.gridGround.isVisible = true;
    }

    cleanTiledGround() {
        this.tileService.hideTiledGround();
        this.cleanPolyline();
    }

    isInitialised() {
        return this.scene ? true : false;
    }

    resizeEngine() {
        this.engine?.resize();
    }


    async takeTopScreenshot(width: number, height: number): Promise<string> {
        return new Promise((resolve, reject) => {
            const c = new TargetCamera(this.screenShotCameraName, new Vector3(0, 1000, 0), this.scene);

            if (this.polylineBorder) {
                this.polylineBorder.edgesWidth = 0.1;
            }
            // lets make lines on top view thinner for ortho camera
            const lines = this.scene.meshes.filter(f => f.name.includes('edges-extrusion'));
            let originalEdgeWidth = 0;
            if (lines.length) {
                originalEdgeWidth = lines[0].edgesWidth;
            }
            lines.forEach(l => {
                l.edgesWidth = 10;
            })

            c.setTarget(new Vector3(0, 0, 0));
            c.mode = Camera.ORTHOGRAPHIC_CAMERA;
            c.orthoLeft = -225;
            c.orthoRight = 225;
            c.orthoBottom = -225;
            c.orthoTop = 225;

            this.scene.setActiveCameraByName(this.screenShotCameraName);
            this.scene.render(true);
            Tools.CreateScreenshotUsingRenderTarget(this.engine, c, { width, height }, (data) => {
                resolve(data);
                if (lines.length && originalEdgeWidth) {
                    lines.forEach(l => {
                        l.edgesWidth = originalEdgeWidth;
                    })
                }
            }, undefined, undefined, true);

        })
    }

    async takePerspectiveScreenshot(position: [number, number, number], target: [number, number, number], fov: number, width: number, height: number): Promise<string> {
        return new Promise((resolve, reject) => {
            const c = new TargetCamera(this.screenShotCameraName, new Vector3(position[0], position[1], position[2]), this.scene);
            c.setTarget(new Vector3(target[0], target[1], target[2]));
            c.fov = fov;
            this.scene.setActiveCameraByName(this.screenShotCameraName);
            this.scene.render(true);
            try {
                Tools.CreateScreenshotUsingRenderTarget(this.engine, c, { width, height }, (data) => {
                    resolve(data);
                    console.log('resolved perspective')
                }, undefined, undefined, true);
            } catch (ex) {
                reject(ex)
            }
        })
    }

    async takeScreenshotGroupId(id: string, width: number, height: number, groupIdLength: number, groupIdHeight: number, groupRotationY: number, threeQuarts: boolean): Promise<string> {
        return new Promise((resolve, reject) => {
            this.changeVisibilityForGroupControlsChildren(false);

            const gctrl = this.modelLoaderService.groupControls.find(gc => gc.groupId === id);
            this.changeVisibilityForGroupControlChildren(gctrl, true);

            let camTargetTransform = new TransformNode("camTarget", this.scene);

            camTargetTransform.parent = gctrl.gizmoMesh;
            camTargetTransform.position.y = groupIdHeight / 2;
            camTargetTransform.position.x += groupIdLength / 2;

            let camPosTransform = new TransformNode("camPosTransform", this.scene);
            camPosTransform.parent = camTargetTransform;

            if (threeQuarts) {
                camPosTransform.translate(new Vector3(1, 1, 1).normalize(), 200);
            } else {
                camPosTransform.translate(new Vector3(0, 1, 0), 200);
                camPosTransform.translate(new Vector3(0, 0, -1), 200);
            }

            const c = new TargetCamera(this.screenShotCameraName, camPosTransform.absolutePosition, this.scene);
            c.fov = 0.4;
            c.setTarget(camTargetTransform.absolutePosition);

            this.scene.setActiveCameraByName(this.screenShotCameraName);
            this.shadowGenerator.getShadowMap().refreshRate = 1;
            this.scene.render(true);
            try {
                console.log('taking screenshot id ', id);
                Tools.CreateScreenshotUsingRenderTarget(this.engine, c, { width, height }, (data) => {
                    resolve(data);
                    console.log('resolved id ', id)
                    this.changeVisibilityForGroupControlsChildren(true);
                    this.shadowGenerator.getShadowMap().refreshRate = 100;

                }, undefined, undefined, true);
            } catch (ex) {
                reject(ex);
            }
        })
    }

    private changeVisibilityForGroupControlsChildren(visible: boolean) {
        this.modelLoaderService.groupControls.forEach(gc => {
            this.changeVisibilityForGroupControlChildren(gc, visible);
        });
    }

    private changeVisibilityForGroupControlChildren(gc: GroupControlsModel, visible: boolean) {
        const children = gc.gizmoMesh.getChildMeshes();
        children.forEach(c => {
            c.isVisible = visible;
        });
    }

    async takeScreenshotTopGroupId(id: string, width: number, height: number, groupIdLength: number, groupIdHeight: number, groupRotationY): Promise<string> {
        return new Promise((resolve, reject) => {

            this.changeVisibilityForGroupControlsChildren(false);
            const gctrl = this.modelLoaderService.groupControls.find(gc => gc.groupId === id);
            this.changeVisibilityForGroupControlChildren(gctrl, true);

            const lines = this.scene.meshes.filter(f => f.name.includes('edges-extrusion'));
            let originalEdgeWidth = 0;
            if (lines.length) {
                originalEdgeWidth = lines[0].edgesWidth;
            }
            lines.forEach(l => {
                l.edgesWidth = 10;
            })

            let camTargetTransform = new TransformNode("camTarget", this.scene);

            camTargetTransform.parent = gctrl.gizmoMesh;
            camTargetTransform.position.y = groupIdHeight / 2;
            camTargetTransform.position.x += groupIdLength / 2;

            let camPosTransform = new TransformNode("camPosTransform", this.scene);
            camPosTransform.parent = camTargetTransform;

            camPosTransform.translate(new Vector3(0, 1, 0), 100);

            const c = new TargetCamera(this.screenShotCameraName, camPosTransform.absolutePosition, this.scene);
            c.setTarget(camTargetTransform.absolutePosition);
            c.rotation.y = groupRotationY;

            c.mode = Camera.ORTHOGRAPHIC_CAMERA;
            c.orthoLeft = -60;
            c.orthoRight = 60;
            c.orthoBottom = -60;
            c.orthoTop = 60;

            this.scene.setActiveCameraByName(this.screenShotCameraName);
            this.shadowGenerator.getShadowMap().refreshRate = 1;
            this.scene.render(true);
            console.log('taking screenshot top id ', id);
            Tools.CreateScreenshotUsingRenderTarget(this.engine, c, { width, height }, (data) => {
                console.log('resolved top id ', id);
                resolve(data);
                c.mode = Camera.PERSPECTIVE_CAMERA;
                this.changeVisibilityForGroupControlsChildren(true);
                if (lines.length && originalEdgeWidth) {
                    lines.forEach(l => {
                        l.edgesWidth = originalEdgeWidth;
                    })
                }
                this.shadowGenerator.getShadowMap().refreshRate = 100;
            }, undefined, undefined, true);
        })
    }

    restoreDefaultCamera() {
        this.scene.setActiveCameraByName(this.defaultCameraName);
        this.scene.activeCamera.fov = 0.8;
        this.engine.resize(true);
        this.scene.render(true);
    }
}
